/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Checkbox,
  Dropdown,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TextInput,
  Spinner,
  Select,
} from "flowbite-react";
import type { FC } from "react";
import { useState, useEffect } from "react";
import { HiSearch } from "react-icons/hi";
import {
  HiChevronLeft,
  HiChevronRight,
  HiCog,
  HiDocumentDownload,
  HiDotsVertical,
  HiExclamationCircle,
  HiFilter,
  HiHome,
  HiPlus,
  HiTrash,
  HiUsers,
} from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import WorkersTable from "../../components/users/WorkersTable";
import TeamManagementModal from "../../components/users/TeamManagementModal";
import { supabase } from "../../lib/supabase"; // Remove TABLES import
import { inviteUser } from "../../services/auth"; // Import the new inviteUser function
import { getRoles } from "../../services/roles"; // Import the getRoles function
import { getAllTeams } from "../../services/teams"; // Import the getAllTeams function
import { getCommissionLevels } from "../../services/commission-levels";

const UserListPage: FC = function () {
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredTotalUsers, setFilteredTotalUsers] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [statusFilter, setStatusFilter] = useState<("active" | "inactive")[]>([
    "active",
  ]);
  const usersPerPage = 20;

  useEffect(() => {
    const fetchTotalUsers = async () => {
      const { count } = await supabase
        .from("workers")
        .select("*", { count: "exact", head: true });
      setTotalUsers(count || 0);
    };

    fetchTotalUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const toggleStatusFilter = (status: "active" | "inactive") => {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((value) => value !== status)
        : [...prev, status]
    );
  };

  const statusFilterSummary =
    statusFilter.length === 2
      ? "Alle Mitarbeiter"
      : statusFilter.length === 1
        ? statusFilter[0] === "active"
          ? "Aktive Mitarbeiter"
          : "Inaktive Mitarbeiter"
        : "Kein Status";

  return (
    <NavbarSidebarLayout isFooter={false} pageTitle="Benutzer">
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="#">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Startseite</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/users/list">Benutzer</BreadcrumbItem>
              <BreadcrumbItem>Liste</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Alle Benutzer
            </h1>
          </div>
          <div className="sm:flex">
            <div className="mb-3 hidden items-center gap-3 sm:mb-0 sm:flex">
              <form className="lg:pr-3">
                <Label htmlFor="users-search" className="sr-only">
                  Suchen
                </Label>
                <div className="relative mt-1 lg:w-64 xl:w-96">
                  <TextInput
                    id="users-search"
                    name="users-search"
                    placeholder="Nach Benutzern suchen"
                    icon={HiSearch}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </form>
              <div className="flex space-x-1">
                <a
                  href="#"
                  className="inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <span className="sr-only">Konfigurieren</span>
                  <HiCog className="text-2xl" />
                </a>
                <a
                  href="#"
                  className="inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <span className="sr-only">Löschen</span>
                  <HiTrash className="text-2xl" />
                </a>
                <a
                  href="#"
                  className="inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <span className="sr-only">Bereinigen</span>
                  <HiExclamationCircle className="text-2xl" />
                </a>
                <a
                  href="#"
                  className="inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <span className="sr-only">Einstellungen</span>
                  <HiDotsVertical className="text-2xl" />
                </a>
              </div>
              <Dropdown
                label={
                  <div className="flex items-center gap-2">
                    <HiFilter className="text-base" />
                    <span className="text-xs font-medium">
                      {statusFilterSummary}
                    </span>
                  </div>
                }
                color="light"
                size="xs"
                dismissOnClick={false}
                placement="bottom-end"
                className="status-filter-dropdown"
              >
                <div className="w-56 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Mitarbeiterstatus
                  </p>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="status-filter-active"
                      className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                    >
                      <Checkbox
                        id="status-filter-active"
                        checked={statusFilter.includes("active")}
                        onChange={() => toggleStatusFilter("active")}
                      />
                      <span>Aktive Mitarbeiter</span>
                    </label>
                    <label
                      htmlFor="status-filter-inactive"
                      className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                    >
                      <Checkbox
                        id="status-filter-inactive"
                        checked={statusFilter.includes("inactive")}
                        onChange={() => toggleStatusFilter("inactive")}
                      />
                      <span>Inaktive Mitarbeiter</span>
                    </label>
                  </div>
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    Wählen Sie die gewünschten Status aus.
                  </p>
                </div>
              </Dropdown>
            </div>
            <div className="ml-auto flex items-center space-x-2 sm:space-x-3">
              <AddUserModal />
              <Button 
                color="light" 
                onClick={() => setShowTeamManagement(true)}
              >
                <div className="flex items-center gap-x-3">
                  <HiUsers className="text-xl" />
                  <span>Manage Teams</span>
                </div>
              </Button>
              <Button color="gray">
                <div className="flex items-center gap-x-3">
                  <HiDocumentDownload className="text-xl" />
                  <span>Exportieren</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow">
              <WorkersTable
                searchTerm={searchTerm}
                currentPage={currentPage}
                usersPerPage={usersPerPage}
                statusFilter={statusFilter}
                onFilteredCountChange={setFilteredTotalUsers}
              />
            </div>
          </div>
        </div>
      </div>
      <Pagination
        totalUsers={filteredTotalUsers ?? totalUsers}
        currentPage={currentPage}
        usersPerPage={usersPerPage}
        setCurrentPage={setCurrentPage}
      />
      
      {/* Team Management Modal */}
      <TeamManagementModal
        isOpen={showTeamManagement}
        onClose={() => setShowTeamManagement(false)}
      />
    </NavbarSidebarLayout>
  );
};

const AddUserModal: FC = function () {
  const [isOpen, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    department: "",
    role_id: "",
    commission_level_id: "",
    team_ids: [] as number[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [roles, setRoles] = useState<{ id: number; role_name: string }[]>([]);
  const [commissionLevels, setCommissionLevels] = useState<{ id: number; level_number: number; description: string }[]>([]);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Fetch roles, commission levels and teams when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          setRolesLoading(true);
          setTeamsLoading(true);
          const [rolesData, teamsData, commissionLevelsData] = await Promise.all([
            getRoles(),
            getAllTeams(),
            getCommissionLevels(),
          ]);
          setRoles(rolesData);
          setTeams(teamsData);
          setCommissionLevels(commissionLevelsData);
        } catch (err) {
          console.error("Error loading data:", err);
        } finally {
          setRolesLoading(false);
          setTeamsLoading(false);
        }
      };

      fetchData();
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues = Array.from(e.target.selectedOptions, option => Number(option.value));
    setFormData((prev) => ({
      ...prev,
      team_ids: selectedValues,
    }));
  };

  const handleInvite = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError("Bitte füllen Sie alle erforderlichen Felder aus");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the inviteUser service function
      const result = await inviteUser({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        department: formData.department,
        phone: formData.phone,
        role_id: formData.role_id ? parseInt(formData.role_id) : undefined,
        commission_level_id: formData.commission_level_id ? parseInt(formData.commission_level_id) : undefined,
      });

      // If teams were selected, assign them to the worker
      if (formData.team_ids.length > 0) {
        // Note: We'll need to import updateWorkerTeams from teams service
        const { updateWorkerTeams } = await import("../../services/teams");
        await updateWorkerTeams(result.worker.id, formData.team_ids);
      }

      setSuccess("Benutzer wurde erfolgreich eingeladen!");

      // Reset form data after successful invitation
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        department: "",
        role_id: "",
        commission_level_id: "",
        team_ids: [],
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess(null);
        setOpen(false);
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Ein Fehler ist aufgetreten"
      );
      console.error("Error inviting user:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button color="primary" onClick={() => setOpen(true)}>
        <div className="flex items-center gap-x-3">
          <HiPlus className="text-xl" />
          Benutzer hinzufügen
        </div>
      </Button>
      <Modal onClose={() => setOpen(false)} show={isOpen}>
        <ModalHeader className="border-b border-gray-200 !p-6 dark:border-gray-700">
          <strong>Neuen Benutzer hinzufügen</strong>
        </ModalHeader>
        <ModalBody>
          {error && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-3 text-green-800">
              {success}
            </div>
          )}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="firstName">Vorname</Label>
              <div className="mt-1">
                <TextInput
                  id="firstName"
                  name="firstName"
                  placeholder="Max"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="sentry-unmask"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="lastName">Nachname</Label>
              <div className="mt-1">
                <TextInput
                  id="lastName"
                  name="lastName"
                  placeholder="Mustermann"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="sentry-unmask"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <div className="mt-1">
                <TextInput
                  id="email"
                  name="email"
                  placeholder="beispiel@firma.de"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="sentry-unmask"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Telefonnummer</Label>
              <div className="mt-1">
                <TextInput
                  id="phone"
                  name="phone"
                  placeholder="z.B. +(49)123 456789"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="sentry-unmask"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="role_id">Rolle</Label>
              <div className="mt-1">
                <Select
                  id="role_id"
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleChange}
                  disabled={rolesLoading}
                >
                  <option value="">Rolle auswählen</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </Select>
                {rolesLoading && (
                  <div className="mt-2 text-xs text-gray-500">
                    Rollen werden geladen...
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="commission_level_id">Provisionslevel</Label>
              <div className="mt-1">
                <Select
                  id="commission_level_id"
                  name="commission_level_id"
                  value={formData.commission_level_id}
                  onChange={handleChange}
                  disabled={rolesLoading}
                >
                  <option value="">Level auswählen</option>
                  {commissionLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      Level {level.level_number} - {level.description}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="team_ids">Teams (optional)</Label>
              <div className="mt-1">
                <Select
                  id="team_ids"
                  name="team_ids"
                  multiple
                  value={formData.team_ids.map(String)}
                  onChange={handleTeamChange}
                  disabled={teamsLoading}
                  className="h-24"
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Select>
                {teamsLoading && (
                  <div className="mt-2 text-xs text-gray-500">
                    Teams werden geladen...
                  </div>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  Halten Sie Strg/Cmd gedrückt, um mehrere Teams auszuwählen
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="department">Abteilung</Label>
              <div className="mt-1">
                <TextInput
                  id="department"
                  name="department"
                  placeholder="Entwicklung"
                  value={formData.department}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleInvite} disabled={loading}>
            {loading ? (
              <div className="flex items-center gap-x-3">
                <Spinner size="sm" /> Einladung wird gesendet...
              </div>
            ) : (
              "Benutzer einladen"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export const Pagination: FC<{
  totalUsers: number;
  currentPage: number;
  usersPerPage: number;
  setCurrentPage: (page: number) => void;
}> = function ({ totalUsers, currentPage, usersPerPage, setCurrentPage }) {
  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="sticky right-0 bottom-0 w-full items-center border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex sm:justify-between">
      <div className="mb-4 flex items-center sm:mb-0">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
            currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
          }`}
          aria-label="Vorherige Seite"
        >
          <HiChevronLeft className="text-2xl" />
        </button>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`mr-2 inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
            currentPage === totalPages ? "cursor-not-allowed opacity-50" : ""
          }`}
          aria-label="Nächste Seite"
        >
          <HiChevronRight className="text-2xl" />
        </button>
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
          Zeige&nbsp;
          <span className="font-semibold text-gray-900 dark:text-white">
            {(currentPage - 1) * usersPerPage + 1}
          </span>
          &nbsp;bis&nbsp;
          <span className="font-semibold text-gray-900 dark:text-white">
            {Math.min(currentPage * usersPerPage, totalUsers)}
          </span>
          &nbsp;von&nbsp;
          <span className="font-semibold text-gray-900 dark:text-white">
            {totalUsers}
          </span>
        </span>
      </div>
      <div className="flex items-center space-x-3">
        <Button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`inline-flex flex-1 items-center justify-center rounded-lg bg-primary-700 py-2 px-3 text-center text-sm font-medium text-white hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 ${
            currentPage === 1 ? "cursor-not-allowed" : ""
          }`}
        >
          <HiChevronLeft className="mr-1 text-base" />
          Zurück
        </Button>
        <Button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`inline-flex flex-1 items-center justify-center rounded-lg bg-primary-700 py-2 px-3 text-center text-sm font-medium text-white hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 ${
            currentPage === totalPages ? "cursor-not-allowed" : ""
          }`}
        >
          Weiter
          <HiChevronRight className="ml-1 text-base" />
        </Button>
      </div>
    </div>
  );
};

export default UserListPage;
